import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useQuery } from "react-query";
import { useSessionStorage } from "../../../hooks";
import { searchAccounts, SERVER_PAGE_SIZE } from "../../../api/services";

type State = {
  query: string;
  page: number;
};
type Action =
  | { type: "setQuery"; payload: string }
  | { type: "setPage"; payload: number };

export const STORAGE_KEY = "accountSearch";
const PAGE_SIZE = 20; //Number of rows displayed in client pagination

export const useAccountSearch = () => {
  // Initialised first with defaults so that we can use these values for the
  // initialisation of our local states.
  const [initialState, persistState] = useSessionStorage<State>(
    STORAGE_KEY,
    { page: 1, query: "" },
    {
      removeOnUnmount: true,
    },
  );

  // The state we are directly working with in the UI, populated from either
  // default values or session storage
  const [{ query, page }, dispatch] = useReducer(
    (state: State, action: Action) => {
      switch (action.type) {
        case "setQuery":
          return {
            query: action.payload,
            page: 1,
          };
        case "setPage":
          return {
            ...state,
            page: action.payload,
          };
        /* istanbul ignore next */
        default:
          return state;
      }
    },
    initialState,
  );

  const setPage = useCallback(
    (page: number) =>
      dispatch({
        type: "setPage",
        payload: page,
      }),
    [dispatch],
  );
  const setQuery = useCallback(
    (query: string) =>
      dispatch({
        type: "setQuery",
        payload: query,
      }),
    [dispatch],
  );

  const { isFetching, status, error, data } = useQuery(
    [
      "accountSearch",
      {
        query,
        page: 1, // first 100 results from server
      },
    ],
    searchAccounts,
    {
      enabled: !!query,
      refetchOnWindowFocus: false,
    },
  );

  const results = useMemo(() => {
    return data
      ? data.results.slice(PAGE_SIZE * (page - 1), PAGE_SIZE * page)
      : data;
  }, [data, page]);

  const isLastPage = data
    ? page * PAGE_SIZE >= Math.min(data?.totalCount, SERVER_PAGE_SIZE)
    : false;

  // Implements the persistence by updating the "search" sessionStorage key
  // whenever page or term changes, we do this just before the return so that
  // all local variables are available to us here in their final form.
  useEffect(() => {
    persistState({
      page,
      query,
    });
  }, [page, query, persistState]);

  // Consumer API
  return {
    // Pagination Handlers
    setPage,
    page,
    pageSize: PAGE_SIZE,
    isLastPage,
    // Request Handlers
    isFetching,
    status,
    error,
    results,
    resultsCount: data?.totalCount,
    // Search term handling
    query,
    setQuery,
  };
};
